import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as XLSX from 'xlsx';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from 'contexts/UserContext';
import { api } from 'services/api-client';
import { useExercisesStore } from '../../stores/useExercisesStore';

export function useExerciseExcelImport(params: { onAfterImport?: () => void; setLoading: (v: boolean) => void }) {
  const { onAfterImport, setLoading } = params;
  const { showToast } = useToast();
  const { coach } = useUser();
  const intl = useIntl();
  const invalidateExercises = useExercisesStore((s) => s.invalidate);
  const [analysisDialogVisible, setAnalysisDialogVisible] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [totalSize, setTotalSize] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // (#27) Mapeo de columnas: tras elegir el archivo detectamos sus headers y pedimos
  // al coach que los asocie a los campos del modelo antes de mandar al backend.
  const [mappingDialogVisible, setMappingDialogVisible] = useState(false);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const fileUploadRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const clearAll = useCallback(() => {
    setSelectedFile(null);
    setTotalSize(0);
    setAnalysisData(null);
    setAnalysisDialogVisible(false);
    setMappingDialogVisible(false);
    setDetectedColumns([]);
    setPendingFile(null);
    if (fileUploadRef.current?.clear) {
      fileUploadRef.current.clear();
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const onTemplateSelect = useCallback(
    (e: { files: Record<string, File> }) => {
      let _totalSize = totalSize;
      const files = e.files;
      Object.keys(files).forEach((key) => {
        _totalSize = files[key]?.size || 0;
      });
      setTotalSize(_totalSize);
      setSelectedFile(files[0] || null);
    },
    [totalSize]
  );

  const onTemplateUpload = useCallback((_e: any) => {
    // noop placeholder for PrimeReact callbacks
  }, []);

  const onTemplateError = useCallback((e: any) => {
    setTotalSize(0);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    console.error('Error during upload:', e);
  }, []);

  const onTemplateClear = useCallback(() => {
    setSelectedFile(null);
    setTotalSize(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // Paso 1: detectar las columnas del archivo (FE, con SheetJS) y abrir el mapeo.
  const uploadHandler = useCallback(
    async ({ files }: { files: File[] | Record<string, File> }) => {
      const file = Array.isArray(files) ? files[0] : (files as any)[0];
      if (!file) return;
      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
        if (!sheet) {
          showToast('error', 'Error', intl.formatMessage({ id: 'coach.importExercises.map.noColumns' }));
          return;
        }
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false }) as unknown[][];
        const headers = (rows[0] || []).map((h) => (h == null ? '' : String(h).trim())).filter((h) => h.length > 0);
        if (!headers.length) {
          showToast('error', 'Error', intl.formatMessage({ id: 'coach.importExercises.map.noColumns' }));
          return;
        }
        setPendingFile(file);
        setDetectedColumns(headers);
        setMappingDialogVisible(true);
      } catch (error: any) {
        onTemplateError(error);
        console.error('Error reading excel headers', error);
        showToast('error', 'Error', error.message);
      }
    },
    [showToast, onTemplateError, intl]
  );

  // Paso 2: con el mapeo confirmado, mandamos archivo + mapeo al backend a analizar.
  const handleMappingConfirm = useCallback(
    async (mapping: Record<string, string>) => {
      if (!pendingFile) return;
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('mapping', JSON.stringify(mapping));
      try {
        setLoading(true);
        setMappingDialogVisible(false);
        const { data } = await api.exercise.analyzeExcelFile(coach!.id, formData);
        setAnalysisDialogVisible(true);
        setAnalysisData(data);
      } catch (error: any) {
        onTemplateError(error);
        console.error('Error analyzing excel file', error);
        showToast('error', 'Error', error.message);
      } finally {
        setLoading(false);
      }
    },
    [pendingFile, coach, onTemplateError, setLoading, showToast]
  );

  const handleMappingCancel = useCallback(() => {
    clearAll();
  }, [clearAll]);

  const handleAnalysisCancel = useCallback(() => {
    clearAll();
  }, [clearAll]);

  const handleAnalysisConfirm = useCallback(
    async (selectedItems?: any[]) => {
      try {
        setLoading(true);

        // Si se pasaron items seleccionados, filtrar los datos según la selección
        let newExercises = analysisData?.exercisesToCreate || [];
        let updateExercises = analysisData?.exercisesToUpdate || [];

        if (selectedItems && selectedItems.length > 0) {
          const selectedNames = new Set(selectedItems.map((item: any) => item.name));
          newExercises = newExercises.filter((ex: any) => selectedNames.has(ex.name));
          updateExercises = updateExercises.filter((ex: any) => selectedNames.has(ex.name));
        }

        // Deduplicar por nombre (case-insensitive) para evitar crear duplicados
        const seenNames = new Set<string>();
        newExercises = newExercises.filter((ex: any) => {
          const normalized = ex.name?.trim().toLowerCase();
          if (seenNames.has(normalized)) return false;
          seenNames.add(normalized);
          return true;
        });

        const importData = {
          newExercises,
          updateExercises
        };
        await api.exercise.processImportExercises(importData);

        invalidateExercises();
        clearAll();
        onAfterImport && onAfterImport();
      } catch (error: any) {
        console.error('Error processing import exercises', error);
        showToast('error', 'Error', error.message);
        clearAll();
      } finally {
        setLoading(false);
      }
    },
    [analysisData, clearAll, invalidateExercises, onAfterImport, setLoading, showToast]
  );

  return {
    // refs
    fileUploadRef,
    fileInputRef,
    // state
    selectedFile,
    totalSize,
    analysisDialogVisible,
    analysisData,
    mappingDialogVisible,
    detectedColumns,
    // actions
    setAnalysisData,
    onTemplateSelect,
    uploadHandler,
    onTemplateUpload,
    onTemplateError,
    onTemplateClear,
    handleAnalysisCancel,
    handleAnalysisConfirm,
    handleMappingConfirm,
    handleMappingCancel
  };
}
